import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiError, type FamilyInviteView } from "@keydials/api-client";
import { api, useAuth } from "../auth/AuthContext";
import { TermsAcceptance, type TermsState } from "../components/TermsAcceptance";
import { joinHeading } from "./familyJoinCopy";

type Step = "loading" | "dead" | "invite" | "code";

/**
 * The front door for a family-pack invitation (KEY-651): /family/join/<token>.
 *
 * Sessionless like /join/ and /guest/: the relative has no account until they accept, and signing in cannot
 * create one. So accepting is what creates it — their own organisation, plan and dial, for the address the
 * invitation was sent to — and the page then asks for a sign-in code for that address and takes the one-time
 * code right here. The inbox is still the credential: a forwarded link can set the dial up, but only the
 * invited address can get into it.
 */
export function FamilyJoin() {
  const location = useLocation();
  const navigate = useNavigate();
  const { requestCode, verifyCode } = useAuth();
  const token = location.pathname.split("/")[3] ?? "";

  const [step, setStep] = useState<Step>("loading");
  const [invite, setInvite] = useState<FamilyInviteView | null>(null);
  const [acceptedVersion, setAcceptedVersion] = useState<string | null>(null);
  const [termsReload, setTermsReload] = useState(0);
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loaded = useRef(false);

  const onTerms = useCallback((state: TermsState) => setAcceptedVersion(state.acceptedVersion), []);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    api
      .getFamilyInvite(token)
      .then((view) => {
        setInvite(view);
        setEmail(view?.email ?? "");
        setStep(view ? "invite" : "dead");
      })
      .catch(() => setStep("dead"));
  }, [token]);

  const sendCode = async (to: string) => {
    await requestCode(to);
    setStep("code");
  };

  const accept = async () => {
    if (busy || !acceptedVersion) return;
    setBusy(true);
    setError(null);
    try {
      const claimed = await api.claimFamilyInvite(token, { termsVersion: acceptedVersion, ageConfirmed: true });
      setEmail(claimed.email);
      await sendCode(claimed.email);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        setStep("dead");
      } else {
        const message = e instanceof ApiError && e.message ? e.message : "Couldn't accept the invitation. Please try again.";
        setError(message);
        // The terms moved on while the page was open: show the new text and ask again.
        if (message.includes("terms have been updated")) setTermsReload((n) => n + 1);
      }
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    if (busy || code.trim().length < 6) return;
    setBusy(true);
    setError(null);
    const ok = await verifyCode(email, code.trim());
    setBusy(false);
    if (ok) {
      navigate("/instances", { replace: true });
    } else {
      setError("That code wasn't recognised. Check the most recent email, or ask for a new code below.");
      setCode("");
    }
  };

  if (step === "loading") {
    return (
      <main className="buy-page">
        <p className="buy-muted">Opening your invitation…</p>
      </main>
    );
  }

  if (step === "dead" || !invite) {
    return (
      <main className="buy-page">
        <header className="buy-header">
          <h1>This invitation link doesn&rsquo;t work any more</h1>
          <p className="buy-muted">
            It may have expired or been withdrawn, or a newer one may have been sent. Ask whoever invited you to send
            it again from their settings.
          </p>
        </header>
      </main>
    );
  }

  if (step === "code") {
    return (
      <main className="buy-page">
        <header className="buy-header">
          <h1>Your dial is ready</h1>
          <p className="buy-muted">
            We&rsquo;ve emailed a 6-digit sign-in code to <strong>{email}</strong>. Enter it below to open your dial.
            There&rsquo;s no password: you&rsquo;ll get a code like this each time you sign in.
          </p>
        </header>
        <section className="buy-checkout">
          <label htmlFor="family-code">Sign-in code</label>
          <input
            id="family-code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            onKeyDown={(e) => {
              if (e.key === "Enter") void verify();
            }}
            inputMode="numeric"
            autoComplete="one-time-code"
            autoFocus
            placeholder="6-digit code"
          />
          {error && <p className="buy-error">{error}</p>}
          <button className="buy-go" type="button" disabled={busy || code.length < 6} onClick={() => void verify()}>
            {busy ? "Signing you in…" : "Open my dial"}
          </button>
          <button
            className="kd-btn kd-btn-sm"
            type="button"
            disabled={busy}
            onClick={() => {
              setError(null);
              void sendCode(email);
            }}
          >
            Send a new code
          </button>
        </section>
      </main>
    );
  }

  if (invite.status === "Joined") {
    return (
      <main className="buy-page">
        <header className="buy-header">
          <h1>You&rsquo;ve already accepted this invitation</h1>
          <p className="buy-muted">
            Your dial is set up for <strong>{invite.email}</strong>. We&rsquo;ll email a sign-in code to that address.
          </p>
        </header>
        <section className="buy-checkout">
          {error && <p className="buy-error">{error}</p>}
          <button className="buy-go" type="button" disabled={busy} onClick={() => void sendCode(invite.email)}>
            Sign in
          </button>
        </section>
      </main>
    );
  }

  if (invite.status === "Ended") {
    return (
      <main className="buy-page">
        <header className="buy-header">
          <h1>This family plan has ended</h1>
          <p className="buy-muted">
            {invite.invitedBy}&rsquo;s plan has ended, so this invitation can&rsquo;t be accepted. If you think
            that&rsquo;s a mistake, let them know.
          </p>
        </header>
      </main>
    );
  }

  return (
    <main className="buy-page">
      <header className="buy-header">
        <h1>{joinHeading(invite)}</h1>
        <p className="buy-muted">
          Hello {invite.invitedName}. {invite.invitedBy} has invited you to Key Dials as part of their {invite.planName}{" "}
          plan. Accept below and your dial is set up for <strong>{invite.email}</strong>, ready to use straight away.
        </p>
      </header>

      <TermsAcceptance
        period={null}
        sponsoredBy={invite.invitedBy}
        disabled={busy}
        reloadKey={termsReload}
        onChange={onTerms}
      />

      <section className="buy-checkout">
        {error && <p className="buy-error">{error}</p>}
        <button className="buy-go" type="button" disabled={busy || !acceptedVersion} onClick={() => void accept()}>
          {busy ? "Setting up your dial…" : "Accept and set up my dial"}
        </button>
        <p className="buy-muted">Nothing is charged to you, and no card is needed.</p>
      </section>
    </main>
  );
}
